import { useState, type DragEvent, type ReactNode } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { Button } from '../../../components/common/Button';
import { Tooltip } from '../../../components/common/Tooltip';
import { SaveToFolderButtons } from './SaveToFolderButtons';
import { SlotActionButtons } from './SlotActionButtons';
import { ImageNameButton } from './ImageNameButton';
import { ImageSlotReorderGrid } from './ImageSlotReorderGrid';
import type { ReorderItem } from './imageSlotReorder';
import { deriveSlotView, type ProductImageSlot, type SlotCapabilities } from './productImageSlots';
import type { GenerationAsset } from '../../../components/generation/state/types';

/**
 * 素材パネルからドラッグされた素材のペイロード (SimpleMaterialsList の onDragStart が
 * `application/json` に入れる buildDragData の形。GenerationAsset と同じキー)
 */
export type DroppedMaterial = GenerationAsset;

/** ドロップイベントから素材パネルのペイロードを取り出す。素材のドラッグでなければ null */
export function readDroppedMaterial(dataTransfer: DataTransfer): DroppedMaterial | null {
  let raw = '';
  try {
    raw = dataTransfer.getData('application/json');
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<DroppedMaterial> | null;
    if (!data || typeof data.id !== 'string' || !data.id) return null;
    if (data.type !== 'temporary' && data.type !== 'cloud') return null;
    return data as DroppedMaterial;
  } catch {
    return null;
  }
}

export type ProductImageSlotsSectionProps = {
  /** [main, sub×8]。表示はこの順 */
  slots: ProductImageSlot[];
  capabilities: SlotCapabilities;
  /** 削除ボタンの出し方。'actions' = SlotActionButtons (差替と一組)、'trash' = 画像左上の専用ゴミ箱 (マニュアル編集モード) */
  deleteButton: 'actions' | 'trash';
  /** 素材パネルの差替対象ハイライト (枠キー) */
  highlightedSlotKey?: string | null;
  /** 空き枠の文言 (既定「画像を選択」) */
  emptyText?: string;
  /** 差替・削除ボタンのツールチップ文言 */
  actionTooltips: (slot: ProductImageSlot) => { replace: string; delete: string };
  isImageSelected: (url: string) => boolean;
  onSelectImage: (slot: ProductImageSlot) => void;
  onReplace: (slot: ProductImageSlot) => void;
  onDelete: (slot: ProductImageSlot) => void;
  onEditName: (slot: ProductImageSlot) => void;
  /**
   * 見出し (名前) のインライン編集の保存。渡すと editName 可の枠で見出しを押すとその場で入力でき、Enter / 枠外で保存、
   * Escape で取り消す (空は名前のクリア)。失敗は投げてよい (入力を残す・通知はページ側)。省略時は見出しは表示だけ
   */
  onSaveName?: (slot: ProductImageSlot, name: string) => Promise<void>;
  onEmptyClick: (slot: ProductImageSlot) => void;
  onDropFile: (slot: ProductImageSlot, file: File) => void;
  /** 素材パネルの素材カードを空き枠にドロップしたとき (fileDrop と同じ枠で受ける)。省略時は無視 */
  onDropMaterial?: (slot: ProductImageSlot, material: DroppedMaterial) => void;
  /** 形式バッジ (slot.formatBadge・マニュアルの SVG) を押したとき: 原本のダウンロード。省略時はバッジを表示だけにする */
  onDownloadOriginal?: (slot: ProductImageSlot) => void;
  saveButtons: {
    isSavedCloud: (url: string) => boolean;
    isSavedTemp: (url: string) => boolean;
    pendingCloud: boolean;
    pendingTemp: boolean;
    onSaveCloud: (url: string) => void;
    onSaveTemp: (url: string) => void;
    /** ボタン文言の動詞 (マニュアル: アップロード)。省略時は SaveToFolderButtons の既定 */
    verb?: '保存' | 'アップロード';
  };
  /** 送信中など、差替ボタンを押せないとき */
  replaceDisabled?: boolean;
  /** 画像の読み込みに失敗したら「No Image」の枠に置き換える (マニュアル) */
  noImageFallback?: boolean;
  reorder: {
    /** null = 開始できる。文字列 = 無効 + ツールチップで理由 */
    disabledReason: string | null;
    /** null = 通常表示。非 null = 並べ替え中 (配列契約は計画 4.3) */
    items: ReorderItem[] | null;
    labelAt: (index: number) => string;
    /** 並びが変わっているか (false なら完了は無効) */
    isDirty: boolean;
    isSubmitting: boolean;
    /** 送信中... / 保存中... */
    submitLabel: string;
    hint: string;
    onStart: () => void;
    onCancel: () => void;
    onChange: (items: ReorderItem[]) => void;
    onComplete: () => void;
  };
  /** 下部アクション行に足すもの */
  extraActions?: ReactNode;
  /** メイン画像も追加画像と同じ大きさのカードにして 1 つのグリッドに並べる (マニュアル) */
  uniformLayout?: boolean;
};

/**
 * 商品画像枠セクション (Amazon / マニュアル共通)。
 * メイン画像カード + 画像1〜8 グリッド + 下部のアクション行 (並べ替え) + 並べ替えモード (帯 + グリッド) を描画する。
 * 出し分けは capabilities と deriveSlotView の結果だけで決める (コールバックの有無では決めない)。
 * ダイアログは持たない。計画: docs/requirements/product-detail-page-consolidation-plan.md 4.2
 */
export function ProductImageSlotsSection(props: ProductImageSlotsSectionProps) {
  const { slots, capabilities, reorder, extraActions, uniformLayout } = props;
  // モバイル用3点メニューの開閉 (枠キー)
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [main, ...subs] = slots;
  const isReordering = reorder.items !== null;

  const startReorder = () => {
    setOpenActionMenu(null);
    reorder.onStart();
  };

  const reorderGrid = reorder.items && (
    <ImageSlotReorderGrid
      items={reorder.items}
      labelAt={reorder.labelAt}
      onChange={reorder.onChange}
      disabled={reorder.isSubmitting}
      className={uniformLayout ? 'uniform-slots-grid' : undefined}
    />
  );

  return (
    <div className="image-slots-section">
      {isReordering && main && !main.locked ? (
        /* メインも並べ替える (マニュアル): 全枠を一様グリッドで出す */
        reorderGrid
      ) : uniformLayout ? (
        <div className="image-layout">
          <div className="additional-slots-grid uniform-slots-grid">
            {slots.map((slot) => (
              <SlotCard key={slot.key} {...props} slot={slot} variant="sub" openActionMenu={openActionMenu} setOpenActionMenu={setOpenActionMenu} />
            ))}
          </div>
        </div>
      ) : (
        <div className="image-layout">
          {/* メイン画像エリア（左側） */}
          {main && (
            <div className="main-image-area">
              {isReordering ? (
                <LockedMainCard slot={main} emptyText="画像なし" />
              ) : (
                <SlotCard {...props} slot={main} variant="main" openActionMenu={openActionMenu} setOpenActionMenu={setOpenActionMenu} />
              )}
            </div>
          )}

          {/* 画像エリア（右側、4x2グリッド） */}
          <div className="additional-images-area">
            {isReordering ? (
              reorderGrid
            ) : (
              <div className="additional-slots-grid">
                {subs.map((slot) => (
                  <SlotCard key={slot.key} {...props} slot={slot} variant="sub" openActionMenu={openActionMenu} setOpenActionMenu={setOpenActionMenu} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* フッター: 並べ替え。並べ替え中はキャンセル (左) と完了 (右) に切り替わる */}
      {isReordering ? (
        <div className="reorder-mode-bar" role="status">
          <Button variant="muted" size="sm" className="reorder-mode-bar-cancel" onClick={reorder.onCancel} disabled={reorder.isSubmitting}>
            キャンセル
          </Button>
          <div className="reorder-mode-bar-inner">
            <ArrowLeftRight size={14} />
            <span className="reorder-mode-bar-text">{reorder.hint}</span>
          </div>
          <Button variant="primary" size="sm" className="reorder-mode-bar-done" onClick={reorder.onComplete} disabled={!reorder.isDirty || reorder.isSubmitting}>
            {reorder.isSubmitting ? reorder.submitLabel : '完了'}
          </Button>
        </div>
      ) : (
        <div className="bulk-save-footer">
          {extraActions}
          {capabilities.reorder && (
            <Tooltip content={reorder.disabledReason ?? ''} disabled={!reorder.disabledReason} className="reorder-button-tooltip">
              <Button variant="muted" size="sm" className="reorder-toolbar-button" onClick={startReorder} disabled={!!reorder.disabledReason}>
                <ArrowLeftRight size={14} />
                並べ替え
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}

/** 並べ替え中の固定メイン画像 (Amazon): 操作を省いた表示 */
function LockedMainCard({ slot, emptyText }: { slot: ProductImageSlot; emptyText: string }) {
  return (
    <div className="main-slot-card reorder-locked">
      <div className="slot-header">
        <div className="slot-label">{slot.label}</div>
        <span className="slot-locked-note">固定</span>
      </div>
      <div className="main-slot-preview">
        {slot.url ? (
          <img src={slot.url} alt={slot.label} draggable={false} />
        ) : (
          <div className="empty-slot" style={{ cursor: 'default' }}>
            <span className="empty-text">{emptyText}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** 差し替え送信済みで Amazon のカタログにまだ反映されていないスロットの案内 */
export function ReflectionPendingBar({ uploadedAt }: { uploadedAt: string }) {
  const sent = new Date(uploadedAt);
  const sentLabel = Number.isNaN(sent.getTime())
    ? ''
    : ` (${sent.getHours()}:${String(sent.getMinutes()).padStart(2, '0')}に送信)`;
  return (
    <div className="reflection-pending-bar">
      <Tooltip
        className="reflection-pending-bar-trigger"
        content={`Amazonで処理中です。反映まで数分かかることがあります${sentLabel}。それまでは送信した画像を表示しています`}
      >
        <span>Amazon反映待ち</span>
      </Tooltip>
    </div>
  );
}

type SlotCardProps = ProductImageSlotsSectionProps & {
  slot: ProductImageSlot;
  variant: 'main' | 'sub';
  openActionMenu: string | null;
  setOpenActionMenu: (v: string | null) => void;
};

function SlotCard({
  slot, variant, capabilities, deleteButton, highlightedSlotKey, emptyText = '画像を選択', actionTooltips,
  isImageSelected, onSelectImage, onReplace, onDelete, onEditName, onSaveName, onEmptyClick, onDropFile, onDropMaterial,
  onDownloadOriginal, saveButtons, replaceDisabled, noImageFallback, openActionMenu, setOpenActionMenu,
}: SlotCardProps) {
  const view = deriveSlotView(slot);
  const [dragOver, setDragOver] = useState(false);
  // 見出しのインライン編集 (名前)。editName 可で画像があり、保存の口があるときだけ
  const nameEditable = capabilities.editName && !!slot.url && !!onSaveName;
  const [editingName, setEditingName] = useState<string | null>(null);
  const [savingName, setSavingName] = useState(false);
  const commitName = async () => {
    if (editingName === null || !onSaveName || savingName) return;
    const next = editingName.trim();
    if (next === (slot.name || '')) { setEditingName(null); return; }
    setSavingName(true);
    try {
      await onSaveName(slot, next);
      setEditingName(null);
    } catch {
      // 失敗時は入力を残す (通知はページ側)
    } finally {
      setSavingName(false);
    }
  };
  const cardClass = variant === 'main' ? 'main-slot-card' : 'image-slot-card';
  const previewClass = variant === 'main' ? 'main-slot-preview' : 'slot-preview';
  const selectable = capabilities.select && view.selectable;
  const selected = !!slot.url && !slot.pending && isImageSelected(slot.url);
  const showActions = capabilities.replace && capabilities.delete && deleteButton === 'actions';
  const showTrash = capabilities.delete && deleteButton === 'trash' && !!slot.url;
  const tooltips = actionTooltips(slot);

  // OS からのファイルと、素材パネルからの素材カード (application/json) の両方を受ける
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      onDropFile(slot, file);
      return;
    }
    const material = readDroppedMaterial(e.dataTransfer);
    if (material) onDropMaterial?.(slot, material);
  };

  return (
    <div className={`${cardClass} ${highlightedSlotKey === slot.key ? 'highlighting' : ''}`}>
      <div className="slot-header">
        {editingName !== null ? (
          <input
            className="slot-label slot-label-input"
            value={editingName}
            placeholder={slot.label}
            aria-label={`${slot.label}の名前`}
            disabled={savingName}
            autoFocus
            maxLength={100}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={() => void commitName()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void commitName(); }
              if (e.key === 'Escape') { e.preventDefault(); setEditingName(null); }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : nameEditable ? (
          <button
            type="button"
            className="slot-label slot-label--editable"
            title={slot.name ? `名前: ${slot.name} (押して編集)` : '押して名前を付ける'}
            onClick={(e) => { e.stopPropagation(); setEditingName(slot.name || ''); }}
          >
            {slot.headerLabel ?? slot.label}
          </button>
        ) : (
          <div className="slot-label" title={slot.headerLabel !== undefined ? slot.name || undefined : undefined}>{slot.headerLabel ?? slot.label}</div>
        )}
        {slot.formatBadge && (
          /* ベクター原本 (SVG) のある画像。表示・生成はラスタ版 (PNG) で、原本は押してダウンロード */
          onDownloadOriginal ? (
            <button
              type="button"
              className="slot-format-badge slot-format-badge--button"
              title={`${slot.formatBadge} の原本をダウンロード`}
              aria-label={`${slot.formatBadge} の原本をダウンロード`}
              onClick={(e) => { e.stopPropagation(); onDownloadOriginal(slot); }}
            >
              {slot.formatBadge}
            </button>
          ) : (
            <span className="slot-format-badge" title={`${slot.formatBadge} の原本があります`}>{slot.formatBadge}</span>
          )
        )}
        {capabilities.save && view.showSaveButtons && slot.url && (
          <SaveToFolderButtons
            imageUrl={slot.url}
            savedCloud={saveButtons.isSavedCloud(slot.url)}
            savedTemp={saveButtons.isSavedTemp(slot.url)}
            pendingCloud={saveButtons.pendingCloud}
            pendingTemp={saveButtons.pendingTemp}
            onSaveCloud={saveButtons.onSaveCloud}
            onSaveTemp={saveButtons.onSaveTemp}
            verb={saveButtons.verb}
          />
        )}
      </div>
      <div className={`${previewClass} ${selected ? 'selected' : ''} ${view.showDeletingOverlay ? 'pending-deletion' : ''} ${view.showReplacingOverlay ? 'pending-replacement' : ''}`}>
        {showTrash && (
          <button className="image-trash-button" onClick={() => onDelete(slot)} title="画像を削除">
            <img src="/bin_1_white.svg" alt="削除" />
          </button>
        )}
        {view.shownUrl ? (
          <>
            <img
              src={view.shownUrl}
              alt={slot.imageAlt ?? slot.label}
              onClick={() => selectable && onSelectImage(slot)}
              style={{ cursor: selectable ? 'pointer' : 'default' }}
              onError={noImageFallback ? (e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'no-image-placeholder';
                placeholder.textContent = 'No Image';
                target.parentElement?.appendChild(placeholder);
              } : undefined}
            />
            {view.showDeletingOverlay && (
              <div className="pending-deletion-overlay">
                <span>削除処理中...</span>
              </div>
            )}
            {view.showReplacingOverlay && (
              <div className="pending-replacement-overlay">
                <span>Amazonで差替処理中</span>
              </div>
            )}
            {view.showPendingBar && slot.pending && (
              <ReflectionPendingBar uploadedAt={slot.pending.uploadedAt} />
            )}
            {showActions && (
              <SlotActionButtons
                menuId={slot.key}
                replaceTooltip={tooltips.replace}
                deleteTooltip={tooltips.delete}
                onReplace={() => onReplace(slot)}
                onDelete={() => onDelete(slot)}
                onEditName={capabilities.editName ? () => onEditName(slot) : undefined}
                replaceDisabled={replaceDisabled}
                openActionMenu={openActionMenu}
                setOpenActionMenu={setOpenActionMenu}
              />
            )}
            {capabilities.editName && slot.url && (
              <ImageNameButton
                currentName={slot.name}
                onClick={() => onEditName(slot)}
              />
            )}
          </>
        ) : (
          <div
            className={`empty-slot ${capabilities.emptyClick ? '' : 'catalog-only'}${dragOver ? ' drag-over' : ''}`}
            onClick={() => capabilities.emptyClick && onEmptyClick(slot)}
            style={capabilities.emptyClick ? undefined : { cursor: 'default' }}
            onDrop={capabilities.fileDrop ? handleDrop : undefined}
            onDragOver={capabilities.fileDrop ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
            onDragLeave={capabilities.fileDrop ? () => setDragOver(false) : undefined}
          >
            <span className="plus-icon">+</span>
            <span className="empty-text">{emptyText}</span>
          </div>
        )}
      </div>

      {/* 解像度情報 (Amazon のメイン画像) */}
      {variant === 'main' && slot.resolution && (
        <div className="resolution-info">
          {slot.resolution.width} × {slot.resolution.height}
        </div>
      )}
    </div>
  );
}
